import classNames from "classnames";
import Tooltip from "rc-tooltip";
import React, { useState } from "react";
import { defineMessages, useIntl } from "react-intl";
import { useSelector } from "react-redux";

import { maxTravellersCount } from "shared/cbt/api/travellers";
import { formattedMessageParams } from "shared/messages";

const { pleaseSelect, pleaseType } = defineMessages({
  pleaseSelect: formattedMessageParams("General.pleaseSelect"),
  pleaseType: formattedMessageParams("General.pleaseType"),
});

type TravellersSelectProps = {
  className?: string;
  onChange: (traveller: { label: string; value: string }) => void;
  selectedTravellersIds: Set<string>;
  close: () => void;
  tooltip: (traveller: { label: string; value: string }) => React.ReactNode;
  isDisabled?: boolean;
  onlyCurrentUser?: boolean;
  currentUserEmail?: string;
};

export default function TravellersSelect({
  className,
  onChange,
  selectedTravellersIds,
  close,
  tooltip,
  isDisabled = false,
  onlyCurrentUser = false,
  currentUserEmail = "",
}: TravellersSelectProps) {
  const intl = useIntl();

  const removeSelectedTravellers = (xs) =>
    xs.filter(({ value }) => !selectedTravellersIds.has(value));
  const usersCache = useSelector((state) => state.cbt.users);
  const usersCacheList = usersCache ? Object.values(usersCache) : [];
  const usersVisibleToSelection = onlyCurrentUser
    ? usersCacheList.filter((user) => user?.Email === currentUserEmail)
    : usersCacheList;

  const [value, setValue] = useState("");
  const [selectedOptionIdx, setSelectedOptionIdx] = useState(0);

  const options = Object.values(usersVisibleToSelection);

  const filteredOptions = removeSelectedTravellers(options)?.filter((opt) =>
    opt.label.toLocaleLowerCase().includes(value.toLocaleLowerCase())
  );

  const formatedOptions = filteredOptions
    .sort((a, b) => {
      const textA = a.label.toLowerCase();
      const textB = b.label.toLowerCase();

      if (textA < textB) {
        return -1;
      }
      if (textA > textB) {
        return 1;
      }
      return 0;
    })
    .slice(0, maxTravellersCount);

  return (
    <div className={classNames("travelersSelect", className)}>
      <div className="travelersSelect__input-wrap">
        <input
          autoFocus
          className="travelersSelect__input"
          type="text"
          disabled={isDisabled}
          placeholder={
            formatedOptions.length < maxTravellersCount
              ? intl.formatMessage(pleaseSelect)
              : intl.formatMessage(pleaseType)
          }
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            switch (e.key) {
              case "Enter":
                if (formatedOptions[selectedOptionIdx]) {
                  onChange(formatedOptions[selectedOptionIdx]);
                  setValue("");
                  close();
                }
                break;
              case "ArrowUp":
                setSelectedOptionIdx(
                  (idx) => (idx - 1) % formatedOptions.length
                );
                break;
              case "ArrowDown":
                setSelectedOptionIdx(
                  (idx) => (idx + 1) % formatedOptions.length
                );
                break;
              case "Escape":
                close();
                break;
              default:
            }
          }}
        />
      </div>
      <ul className="travelersSelect__options">
        {((usersCacheList.length > maxTravellersCount && value.length !== 0) ||
        usersCacheList.length <= maxTravellersCount
          ? formatedOptions
          : []
        ).map((opt, idx) => (
          <li key={opt.value}>
            <Tooltip
              overlay={tooltip(opt)}
              mouseLeaveDelay={0}
              mouseEnterDelay={0.2}
            >
              <button
                type="button"
                className={classNames("travelersSelect__option", {
                  "travelersSelect__option--hover": selectedOptionIdx === idx,
                })}
                data-label={opt.label}
                onClick={() => {
                  if (isDisabled) {
                    return;
                  }

                  setValue("");
                  onChange(opt);
                  close();
                }}
                onMouseEnter={() => setSelectedOptionIdx(idx)}
              >
                {opt.label}
              </button>
            </Tooltip>
          </li>
        ))}
      </ul>
    </div>
  );
}
